import { useState, useEffect, useRef } from "react"
import { IoIosArrowRoundBack } from "react-icons/io"
import { useHistory } from "react-router-dom"
import api, { fetchAll } from "../../utils/api"
import { ErrorNotification, SuccessNotification } from "../../utils"
import { FaRegCheckCircle, FaTrash, FaUpload } from "react-icons/fa"
import { useForm } from "react-hook-form"
import { OWNERSHIPTYPES } from "../../components/constants"
import { FiPlusCircle, FiUpload } from "react-icons/fi"
import { IoCloseCircleOutline } from "react-icons/io5"
import useAuth from "../../context/userContext"
import ImageSourceUploadButton from "../../components/ImageSourceUploadButton"
import { trimFormValue, hasTrimmedValue, personNamePatternRule } from "../../utils/formValues"
import { useLocationCascade } from "../../hooks/useLocations"

const trimField = { setValueAs: trimFormValue }

const steps = [
  "Group Information",
  "Farm Information",
]

export default function NewFarmer() {
  const history = useHistory()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [state, setState] = useState({
    user_type: "farmer",
    firstname: "",
    lastname: "",
    email: "",
    password: "password12345",
    contact: "",
    district: "",
    region: "",
    community: "",
    gender: "",
    dob: "",
    education: "",
    doc_url: "",
    farm_name: "",
    farmer: null,
    acres: 1,
    crop: "",
    farm_region: "",
    farm_district: "",
    farm_community: "",
    longitude: "",
    latitude: "",
  })
  const [step, setStep] = useState(0)
  const [profileImagePreview, setProfileImagePreview] = useState(null)
  const [frontImagePreview, setFrontImagePreview] = useState(null)
  const [backImagePreview, setBackImagePreview] = useState(null)
  const [crops, setCrops] = useState([])
  const [groups, setGroups] = useState([])



  // Add state for multiple farms
  const [farms, setFarms] = useState([{
    id: 0,
    farm_name: "",
    farm_size: "",
    farm_region: "",
    farm_district: "",
    farm_community: "",
    primary_crop: "",
    longitude: "",
    latitude: "",
    ownership: "",
    other_crops: [],
    farm_images: [],
    farm_documents: []
  }])
  const [farmErrors, setFarmErrors] = useState([])
  const [currentFarmIndex, setCurrentFarmIndex] = useState(0)
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = useForm({
    mode: "onChange",
    shouldUnregister: false,
    defaultValues: {
      user_type: "farmer",
      firstname: "",
      lastname: "",
      email: "",
      password: "password12345",
      contact: "",
      district: "",
      region: "",
      community: "",
      gender: "",
      dob: "",
      education: "",
      doc_url: "",
      name: "",
      farmer: null,
      acres: 1,
      crop: "",
      region: "",
      district: "",
      community: "",
      longitude: "",
      latitude: "",
      farm_region: "",
      farm_district: "",
      farm_community: "",
      group: "",
      front_image: "",
      back_image: "",
      profile_image: "",
      primary_crop: "",
      ownership: "",
      longitude: "",
      latitude: "",
    }
  })

  const region = watch("region")
  const district = watch("district")
  const community = watch("community")
  const group = watch("group")

  const {
    regionNames,
    districts,
    communities,
  } = useLocationCascade(region, district)

  const currentFarm = farms[currentFarmIndex]
  const {
    districts: farmDistricts,
    communities: farmCommunities,
  } = useLocationCascade(currentFarm?.farm_region, currentFarm?.farm_district)

  const submittingRef = useRef(false)

  const hasStartedFarmData = (farm) =>
    Boolean(
      hasTrimmedValue(farm?.farm_name) ||
      hasTrimmedValue(farm?.farm_size) ||
      hasTrimmedValue(farm?.farm_region) ||
      hasTrimmedValue(farm?.farm_district) ||
      hasTrimmedValue(farm?.farm_community) ||
      farm?.primary_crop ||
      farm?.ownership
    )

  const validateFarmStep = () => {
    const requiredFarmFields = [
      { field: "farm_name", label: "Farm Name" },
      { field: "farm_size", label: "Farm Size" },
      { field: "farm_region", label: "Region" },
      { field: "farm_district", label: "District" },
      { field: "farm_community", label: "Community" },
      { field: "primary_crop", label: "Primary Commodity" },
      { field: "ownership", label: "Ownership Type" }
    ]

    const farmsToValidate = farms.filter(hasStartedFarmData)
    if (farmsToValidate.length === 0) {
      setFarmErrors([])
      return true
    }

    let allFarmsValid = true
    const newFarmErrors = []

    farms.forEach((farm, index) => {
      if (!hasStartedFarmData(farm)) {
        newFarmErrors[index] = {}
        return
      }

      const currentErrors = {}
      requiredFarmFields.forEach(({ field, label }) => {
        const value = farm[field]
        const isEmpty = typeof value === 'string' ? !hasTrimmedValue(value) : !value
        if (isEmpty) {
          allFarmsValid = false
          currentErrors[field] = `${label} is required`
        }
      })
      newFarmErrors[index] = currentErrors
    })

    if (!allFarmsValid) {
      setFarmErrors(newFarmErrors)
      const firstFarmWithError = newFarmErrors.findIndex((err) => Object.keys(err).length > 0)
      if (firstFarmWithError !== -1) {
        setCurrentFarmIndex(firstFarmWithError)
      }
      return false
    }

    setFarmErrors([])
    return true
  }

  const handleFinalSubmit = async () => {
    const step0Fields = [
      "firstname", "lastname", "contact", "region", "district", "community", "gender"
    ]
    const valid = await trigger(step0Fields)
    if (!valid) return
    if (!validateFarmStep()) return
    handleSubmit(onSubmit)()
  }

  const onSubmit = async (data) => {
    if (submittingRef.current) return // Prevent double submit
    submittingRef.current = true
    setLoading(true)
    try {
      // Build the payload from data and farms state
      const formData = {
        firstname: trimFormValue(data.firstname),
        lastname: trimFormValue(data.lastname),
        email: trimFormValue(data.email) || "",
        phone: data.contact?.trim() || "",
        dob: data.dob || null,
        administrator: user?.id,
        gender: data.gender,
        education_level: data.education || null,
        region: data.region,
        district: data.district,
        community: data.community,
        farmer_group_ids: data.group ? [parseInt(data.group)] : [],
        farms_data: farms.filter(hasStartedFarmData).length > 0
          ? farms.filter(hasStartedFarmData).map(farm => ({
          farm_name: trimFormValue(farm.farm_name) || "",
          farm_size: parseFloat(farm.farm_size),
          region: farm.farm_region,
          district: farm.farm_district,
          community: farm.farm_community,
          production_capacity: parseFloat(farm.production_capacity) || 0,
          longitude: farm.longitude ? parseFloat(farm.longitude) : null,
          latitude: farm.latitude ? parseFloat(farm.latitude) : null,
          primary_crop: farm.primary_crop,
          ownership_type: farm.ownership,
          crop_type_ids: farm.other_crops,
        }))
          : []
      }

      // 2. Create the farmer
      const resp = await api.post(
        "farmers/",
        JSON.stringify(formData)
      )

      // 3. If farmer creation is successful, upload farm images for each farm
      if (resp?.data?.id) {
        const farmerId = resp?.data?.id
        const farmerFarms = resp?.data?.my_farms || []

        // Upload farmer profile / Ghana Card images only when files were selected
        const farmerFormData = new FormData()
        if (data?.profile_image) {
          farmerFormData.append('profile_image', data.profile_image)
        }
        if (data?.front_image) {
          farmerFormData.append('ghana_card_image_front', data.front_image)
        }
        if (data?.back_image) {
          farmerFormData.append('ghana_card_image_back', data.back_image)
        }

        if (
          data?.profile_image ||
          data?.front_image ||
          data?.back_image
        ) {
          await api.patch(
            `farmers/${farmerId}/`,
            farmerFormData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          )
        }

        // Upload farm-specific images and documents for each farm
        for (let i = 0; i < farms.length; i++) {
          const farm = farms[i]
          const farmId = farmerFarms[i]?.id

          if (farmId && (farm.farm_images.length > 0 || farm.farm_documents.length > 0)) {
            const imageFormData = new FormData()
            const docsData = new FormData()

            // Append farm images
            farm.farm_images.forEach((file) => {
              imageFormData.append('images', file)
            })

            // Append farm documents
            // farm.farm_documents.forEach((file) => {
            //   imageFormData.append("farm_document", file)
            // })
            docsData.append('farm_document', farm.farm_documents[0])


            imageFormData.append('farm_id', farmId.toString())
            docsData.append('farm_id', farm.id)

            await api.post(
              `farmers/${farmerId}/upload_farm_images/`,
              imageFormData,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            )

            if (farm.farm_documents?.length > 0) {
              await api.patch(
                `/farmers/${farmerId}/update_farm/`,
                docsData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
              )
            }
          }
        }
      }

      setLoading(false)
      submittingRef.current = false
      SuccessNotification("Farmer created successfully")
      history.goBack()
    } catch (error) {
      setLoading(false)
      submittingRef.current = false

      // Handle different error response formats
      const errorData = error.response?.data

      if (errorData?.error) {
        // Handle array of errors
        if (Array.isArray(errorData.error)) {
          const formattedMessage = errorData.error.join('\n')
          ErrorNotification(formattedMessage)
        }
        // Handle object with field-specific errors
        else if (typeof errorData.error === 'object') {
          const fieldErrors = Object.entries(errorData.error).map(([field, messages]) => {
            const fieldName = field.charAt(0).toUpperCase() + field.slice(1)
            const messageList = Array.isArray(messages) ? messages.join(', ') : messages
            return `${fieldName}: ${messageList}`
          })
          ErrorNotification(fieldErrors.join('\n'))
        }
        // Handle string error
        else {
          ErrorNotification(errorData.error)
        }
      }
      // Handle direct field errors (like {"phone":["Farmer with this phone already exists."]})
      else if (errorData && typeof errorData === 'object' && !Array.isArray(errorData)) {
        const fieldErrors = Object.entries(errorData).map(([field, messages]) => {
          const fieldName = field.charAt(0).toUpperCase() + field.slice(1)
          const messageList = Array.isArray(messages) ? messages.join(', ') : messages
          return `${fieldName}: ${messageList}`
        })
        ErrorNotification(fieldErrors.join('\n'))
      }
      // Fallback for other error formats
      else {
        ErrorNotification(error.response?.data?.message || error.message || "An error occurred while creating the farmer")
      }
    }
  }
  const getFormOptions = async () => {
    try {
      const [cropsResponse, groupsData] = await Promise.all([
        api.get('commodities/'),
        fetchAll('groups/')
      ])

      if (cropsResponse.status === 200 && cropsResponse.data) {
        setCrops(cropsResponse.data)
      }

      // Handle the second response as needed
      if (groupsData) {
        setGroups(groupsData)
      }

    } catch (err) {
      console.error('Error fetching data:', err)
    }
  }


  const handleProfileImageFile = (file) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      setProfileImagePreview(reader.result)
      setValue('profile_image', file, { shouldValidate: true })
      trigger('profile_image')
    }
    reader.readAsDataURL(file)
  }

  const handleGhanaCardFrontFile = (file) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      setFrontImagePreview(reader.result)
      setValue('front_image', file, { shouldValidate: true })
      trigger('front_image')
    }
    reader.readAsDataURL(file)
  }

  const handleGhanaCardBackFile = (file) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      setBackImagePreview(reader.result)
      setValue('back_image', file, { shouldValidate: true })
      trigger('back_image')
    }
    reader.readAsDataURL(file)
  }

  // Functions to handle multiple farms
  const addFarm = () => {
    const newFarm = {
      id: farms.length,
      farm_name: "",
      farm_size: "",
      farm_region: "",
      farm_district: "",
      farm_community: "",
      primary_crop: "",
      longitude: "",
      latitude: "",
      ownership: "",
      other_crops: [],
      farm_images: [],
      farm_documents: []
    }
    setFarms([...farms, newFarm])
    setCurrentFarmIndex(farms.length)
  }

  const removeFarm = (index) => {
    if (farms.length > 1) {
      const updatedFarms = farms.filter((_, i) => i !== index)
      setFarms(updatedFarms)
      if (currentFarmIndex >= index) {
        setCurrentFarmIndex(Math.max(0, currentFarmIndex - 1))
      }
    }
  }

  const updateFarm = (index, field, value) => {
    const updatedFarms = [...farms]
    updatedFarms[index] = { ...updatedFarms[index], [field]: value }
    setFarms(updatedFarms)

    // Clear error for this field
    if (farmErrors[index]?.[field]) {
      const updatedErrors = [...farmErrors]
      updatedErrors[index] = { ...updatedErrors[index], [field]: null }
      setFarmErrors(updatedErrors)
    }
  }

  const handleFarmImageChange = (e, farmIndex) => {
    const files = Array.from(e.target.files)
    const updatedFarms = [...farms]
    updatedFarms[farmIndex].farm_images = [...updatedFarms[farmIndex].farm_images, ...files]
    setFarms(updatedFarms)
  }

  const handleFarmDocumentChange = (e, farmIndex) => {
    const files = Array.from(e.target.files)
    const updatedFarms = [...farms]
    updatedFarms[farmIndex].farm_documents = [...updatedFarms[farmIndex].farm_documents, ...files]
    setFarms(updatedFarms)
  }

  const removeFarmImage = (farmIndex, imageIndex) => {
    const updatedFarms = [...farms]
    updatedFarms[farmIndex].farm_images = updatedFarms[farmIndex].farm_images.filter((_, i) => i !== imageIndex)
    setFarms(updatedFarms)
  }

  const removeFarmDocument = (farmIndex, docIndex) => {
    const updatedFarms = [...farms]
    updatedFarms[farmIndex].farm_documents = updatedFarms[farmIndex].farm_documents.filter((_, i) => i !== docIndex)
    setFarms(updatedFarms)
  }

  const toggleFarmCrop = (farmIndex, cropId) => {
    const updatedFarms = [...farms]
    const currentCrops = updatedFarms[farmIndex].other_crops
    if (currentCrops.includes(cropId)) {
      updatedFarms[farmIndex].other_crops = currentCrops.filter(id => id !== cropId)
    } else {
      updatedFarms[farmIndex].other_crops = [...currentCrops, cropId]
    }
    setFarms(updatedFarms)
  }

  useEffect(() => {
    getFormOptions()
  }, [])

  return (
    <>
      <div className="w-full lg:px-10 px-2 h-full py-6">
        <div className="flex items-center justify-between">
          <button type="button" onClick={() => history.goBack()} className="mb-3 flex flex-row items-center">
            <IoIosArrowRoundBack size={25} className="mr-2 dark:text-white" />
            <p className="text-sm font-light capitalize dark:text-white">
              Back
            </p>
          </button>
        </div>

        <h1 className="text-lg font-bold capitalize mb-5 dark:text-white">
          Add New Farmer
        </h1>

        <form className="bg-white h-full px-4 py-2 mt-2 rounded-lg dark:bg-dark">
          <div className="h-[80vh] w-full py-3 overflow-y-auto">
            <h1 className="text-sm font-bold text-yellowcolor capitalize mb-5">
              {steps[step]}
            </h1>

            {{
              0: <div className="w-full h-full space-y-4">
                <div className="w-full space-y-3">
                  <label className="text-sm font-normal capitalize dark:text-white">
                    Profile Image
                  </label>

                  <div className="w-full h-full">
                    {profileImagePreview ? (
                      <div className="relative w-28 h-28">
                        <img
                          src={profileImagePreview}
                          alt="profile"
                          className="w-28 h-28 rounded-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setProfileImagePreview(null)
                            setState({ ...state, profile_image: null })
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-28 h-28">
                        <svg width="132" height="132" viewBox="0 0 132 132" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g filter="url(#filter0_dd_1545_118337)">
                            <rect x="16" y="4" width="100" height="100" rx="50" fill="white" />
                            <rect x="14" y="2" width="104" height="104" rx="52" stroke="#E2E2E2" stroke-width="4" />
                            <path d="M66.3701 56.9048C66.1542 56.874 65.8767 56.874 65.6301 56.9048C60.2034 56.7198 55.8867 52.2798 55.8867 46.8223C55.8867 41.2415 60.3884 36.709 66.0001 36.709C71.5809 36.709 76.1134 41.2415 76.1134 46.8223C76.0826 52.2798 71.7967 56.7198 66.3701 56.9048Z" stroke="#E2E2E2" stroke-width="3.46875" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M86.7821 77.2555C81.2937 82.2814 74.0171 85.3339 66.0004 85.3339C57.9837 85.3339 50.7071 82.2814 45.2188 77.2555C45.5271 74.3572 47.3771 71.5205 50.6762 69.3005C59.1246 63.6889 72.9379 63.6889 81.3246 69.3005C84.6237 71.5205 86.4737 74.3572 86.7821 77.2555Z" stroke="#E2E2E2" stroke-width="3.46875" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M65.9993 85.3346C83.0281 85.3346 96.8327 71.5301 96.8327 54.5013C96.8327 37.4725 83.0281 23.668 65.9993 23.668C48.9706 23.668 35.166 37.4725 35.166 54.5013C35.166 71.5301 48.9706 85.3346 65.9993 85.3346Z" stroke="#E2E2E2" stroke-width="3.46875" stroke-linecap="round" stroke-linejoin="round" />
                          </g>
                          <defs>
                            <filter id="filter0_dd_1545_118337" x="0" y="0" width="132" height="132" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                              <feFlood flood-opacity="0" result="BackgroundImageFix" />
                              <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                              <feMorphology radius="2" operator="erode" in="SourceAlpha" result="effect1_dropShadow_1545_118337" />
                              <feOffset dy="4" />
                              <feGaussianBlur stdDeviation="3" />
                              <feColorMatrix type="matrix" values="0 0 0 0 0.0627451 0 0 0 0 0.0941176 0 0 0 0 0.156863 0 0 0 0.03 0" />
                              <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1545_118337" />
                              <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                              <feMorphology radius="4" operator="erode" in="SourceAlpha" result="effect2_dropShadow_1545_118337" />
                              <feOffset dy="12" />
                              <feGaussianBlur stdDeviation="8" />
                              <feColorMatrix type="matrix" values="0 0 0 0 0.0627451 0 0 0 0 0.0941176 0 0 0 0 0.156863 0 0 0 0.08 0" />
                              <feBlend mode="normal" in2="effect1_dropShadow_1545_118337" result="effect2_dropShadow_1545_118337" />
                              <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_1545_118337" result="shape" />
                            </filter>
                          </defs>
                        </svg>
                      </div>
                    )}
                    <div className="mt-2">
                      <ImageSourceUploadButton
                        inputId="profile_image"
                        accept="image/*"
                        captureFacing="user"
                        onFileSelect={handleProfileImageFile}
                        className="cursor-pointer"
                      >
                        <div className="px-2 w-40 flex flex-row items-center gap-4 text-sm py-2 bg-yellowcolor text-black rounded-lg border border-yellowcolor">
                          <FiUpload size={16} />
                          Click to upload
                        </div>
                      </ImageSourceUploadButton>
                    </div>
                  </div>

                  <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-7">
                    <div className="w-full space-y-3">
                      <label className="text-sm font-normal capitalize dark:text-white">
                        First Name<span className="text-red-500">*</span>
                      </label>

                      <input
                        type="text"
                        name="firstname"
                        id="firstname"
                        placeholder="Enter your first name"
                        className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                        {...register("firstname", {
                          ...trimField,
                          required: "First name is required",
                          pattern: personNamePatternRule('First name'),
                          minLength: {
                            value: 1,
                            message: "First name must be at least 3 characters long"
                          }
                        })}
                      />

                      {errors.firstname && <p className="text-red-500 text-xs mt-1">{errors.firstname.message}</p>}
                    </div>

                    <div className="w-full space-y-3">
                      <label className="text-sm font-normal capitalize dark:text-white">
                        Last Name<span className="text-red-500">*</span>
                      </label>

                      <input
                        type="text"
                        name="lastname"
                        id="lastname"
                        placeholder="Enter your last name"
                        className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                        {...register("lastname", {
                          ...trimField,
                          required: "Last name is required",
                          pattern: personNamePatternRule('Last name'),
                          minLength: {
                            value: 1,
                            message: "Last name must be at least 3 characters long"
                          }
                        })}
                      />
                      {errors.lastname && <p className="text-red-500 text-xs mt-1">{errors.lastname.message}</p>}
                    </div>


                    <div className="w-full space-y-3">
                      <label className="text-sm font-normal capitalize dark:text-white">
                        Email
                      </label>

                      <input
                        type="email"
                        id="email"
                        placeholder="Enter your email"
                        className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                        {...register("email", {
                          ...trimField,
                          pattern: {
                            value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                            message: "Invalid email format"
                          }
                        })}
                      />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                    </div>


                    <div className="w-full space-y-3">
                      <label htmlFor="phone" className="block text-base font-medium text-gray-700 mb-1 dark:text-white">
                        Phone Number
                      </label>
                      <div className="flex items-center">
                        <span className="mr-2 bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 pl-3 pr-6 flex items-center">
                          <img src="https://upload.wikimedia.org/wikipedia/commons/1/19/Flag_of_Ghana.svg" alt="Ghana" className="w-6 h-4 rounded-sm " />
                          <span className="px-2">+233</span>
                        </span>

                        <input
                          type="tel"
                          name="contact"
                          id="contact"
                          {...register("contact", {
                            ...trimField,
                            validate: (value) => {
                              const trimmed = String(value || "").trim()
                              if (!trimmed) return true
                              if (!/^[0-9]+$/.test(trimmed)) {
                                return "Phone number must contain only numbers"
                              }
                              return true
                            }
                          })}
                          placeholder="eg. 0540977343"
                          className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                        />
                      </div>

                      {errors.contact && <p className="text-red-500 text-xs mt-1">{errors.contact.message}</p>}
                    </div>

                    <div className="w-full space-y-3">
                      <label className="text-sm font-normal capitalize dark:text-white">
                        Date of Birth
                      </label>

                      <input
                        type="date"
                        name="dob"
                        id="dob"
                        className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                        {...register("dob", {
                          validate: (value) => {
                            if (!value) return true
                            const today = new Date()
                            const birthDate = new Date(value)
                            let age = today.getFullYear() - birthDate.getFullYear()
                            const m = today.getMonth() - birthDate.getMonth()
                            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                              age--
                            }
                            return age >= 18 || "You must be at least 18 years old"
                          },
                          min: {
                            value: "1900-01-01",
                            message: "Date of birth must be at least 1900-01-01"
                          }
                        })}
                      />

                    </div>

                    <div className="w-full space-y-3">
                      <label className="text-sm font-normal capitalize dark:text-white">
                        Level Of Education
                      </label>

                      <select
                        name="education"
                        id="education"
                        className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                        {...register("education")}
                      >
                        <option value="">Select</option>
                        <option value="primary">Primary</option>
                        <option value="secondary">Secondary</option>
                        <option value="tertiary">Tertiary</option>
                      </select>
                    </div>

                    <div className="w-full space-y-3">
                      <label className="text-sm font-normal capitalize dark:text-white">
                        Region<span className="text-red-500">*</span>
                      </label>

                      <select
                        id="region"
                        className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                        {...register("region", { required: "Region is required" })}
                        onChange={(e) => {
                          setValue("region", e.target.value, { shouldValidate: true })
                          setValue("district", "", { shouldValidate: true })
                          setValue("community", "", { shouldValidate: true })
                        }}
                      >
                        <option value="">Select Region</option>
                        {regionNames.map((regionName, index) => (
                          <option key={index} value={regionName}>
                            {regionName}
                          </option>
                        ))}
                      </select>
                      {errors.region && <p className="text-red-500 text-xs mt-1">{errors.region.message}</p>}
                    </div>

                    <div className="w-full space-y-3">
                      <label className="text-sm font-normal capitalize dark:text-white">
                        District<span className="text-red-500">*</span>
                      </label>

                      <select
                        id="district"
                        className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                        {...register("district", { required: "District is required" })}
                        onChange={(e) => {
                          setValue("district", e.target.value, { shouldValidate: true })
                          setValue("community", "", { shouldValidate: true })
                        }}
                      >
                        <option value="">Select District</option>
                        {districts?.map((item, index) => (
                          <option key={index} value={item.name}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                      {errors.district && <p className="text-red-500 text-xs mt-1">{errors.district.message}</p>}
                    </div>

                    <div className="w-full space-y-3">
                      <label className="text-sm font-normal capitalize dark:text-white">
                        Community<span className="text-red-500">*</span>
                      </label>

                      <select
                        name="community"
                        id="community"
                        className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                        value={community}
                        onChange={e => setValue("community", e.target.value)}
                        {...register("community", { required: "Community is required" })}
                      >
                        <option value="">Select Community</option>
                        {communities?.map((item, index) => (
                          <option key={index} value={item}>
                            {item}
                          </option>
                        ))}
                      </select>

                      {errors.community && <p className="text-red-500 text-xs mt-1">{errors.community.message}</p>}
                    </div>

                    <div className="w-full space-y-3">
                      <label className="text-sm font-normal capitalize dark:text-white">
                        Group (Optional)
                      </label>

                      <select
                        name="group"
                        id="group"
                        className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                        value={group}
                        onChange={(e) => {
                          setValue("group", e.target.value)
                          setState({ ...state, group: e.target.value })
                        }}
                      >
                        <option value="">Select Group</option>
                        {groups?.map((grp) => <option className="capitalize" key={grp?.id} value={grp?.id}>
                          {grp?.group_name}
                        </option>)}
                      </select>
                    </div>

                    <div className="w-full space-y-3">
                      <label className="text-sm font-normal capitalize dark:text-white">
                        Gender<span className="text-red-500">*</span>
                      </label>

                      <div className="flex flex-row items-center gap-2">
                        <div className="flex flex-row items-center gap-2">
                          <input type="radio" name="gender" id="male" value="male" {...register("gender", {
                            required: "Gender is required",
                          })}
                            className="form-radio text-yellowcolor focus:ring-yellowcolor"
                          />
                          <label htmlFor="male" className=" dark:text-white">Male</label>
                        </div>

                        <div className="flex flex-row items-center gap-2">
                          <input
                            type="radio"
                            name="gender"
                            id="female"
                            value="female"
                            {...register("gender", {
                              required: "Gender is required",
                            })}
                            className="form-radio text-yellowcolor focus:ring-yellowcolor"
                          />
                          <label htmlFor="female" className=" dark:text-white">Female</label>
                        </div>
                      </div>
                      {errors.gender && <p className="text-red-500 text-xs mt-1">{errors.gender.message}</p>}
                    </div>  <div></div>


                    <div className="w-full space-y-3 mt-6">
                      <label className="text-sm font-normal capitalize dark:text-white">
                        Front Image of Government ID
                      </label>

                      <ImageSourceUploadButton
                        inputId="front_image"
                        accept="image/png,image/jpeg,image/*"
                        captureFacing="environment"
                        onFileSelect={handleGhanaCardFrontFile}
                        className="text-sm h-64 w-full border border-dotted border-gray-200 rounded-md flex flex-col items-center justify-center cursor-pointer"
                      >
                        {frontImagePreview ? (
                          <div className="relative w-full h-full">
                            <img
                              src={frontImagePreview}
                              alt="Ghana Card Front"
                              className="w-full h-full object-cover rounded-lg overflow-hidden"
                            />
                            <div className="absolute bg-black/40 inset-0 rounded-lg overflow-hidden" />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                setFrontImagePreview(null)
                                setState({ ...state, front_image: null })
                                setValue('front_image', '', { shouldValidate: true })
                              }}
                              className="absolute bottom-4 left-48 bg-white shadow-2xl text-yellowcolor text-xs font-bold rounded-full p-3"
                            >
                              Remove Image
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="w-10 h-10 rounded-full bg-yellowcolor/20 flex flex-row items-center justify-center">
                              <div className="w-7 h-7 rounded-full bg-yellowcolor/20 flex flex-row items-center justify-center">
                                <FaUpload size={20} className="text-yellowcolor" />
                              </div>
                            </div>
                            <span className="font-semibold text-yellowcolor">Click to upload</span>
                          </>
                        )}
                      </ImageSourceUploadButton>
                    </div>

                    <div className="w-full space-y-3 mt-6">
                      <label className="text-sm font-normal capitalize dark:text-white">
                        Back Image of Government ID
                      </label>

                      <ImageSourceUploadButton
                        inputId="back_image"
                        accept="image/png,image/jpeg,image/*"
                        captureFacing="environment"
                        onFileSelect={handleGhanaCardBackFile}
                        className="text-sm h-64 w-full border border-dotted border-gray-200 rounded-md flex flex-col items-center justify-center cursor-pointer"
                      >
                        {backImagePreview ? (
                          <div className="relative w-full h-full">
                            <img
                              src={backImagePreview}
                              alt="Ghana Card Back"
                              className="w-full h-full object-cover rounded-lg overflow-hidden"
                            />
                            <div className="absolute bg-black/40 inset-0 rounded-lg overflow-hidden" />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                setBackImagePreview(null)
                                setState({ ...state, back_image: null })
                                setValue('back_image', '', { shouldValidate: true })
                              }}
                              className="absolute bottom-4 left-48 bg-white shadow-2xl text-yellowcolor text-xs font-bold rounded-full p-3"
                            >
                              Remove Image
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="w-10 h-10 rounded-full bg-yellowcolor/20 flex flex-row items-center justify-center">
                              <div className="w-7 h-7 rounded-full bg-yellowcolor/20 flex flex-row items-center justify-center">
                                <FaUpload size={20} className="text-yellowcolor" />
                              </div>
                            </div>
                            <span className="font-semibold text-yellowcolor">Click to upload</span>
                          </>
                        )}
                      </ImageSourceUploadButton>
                    </div>
                  </div>

                  <div className="h-16"></div>
                </div>
              </div>,
              1: <div className="w-full h-full">
                {/* Accordion-style Farm List */}
                {/* Add Farm Button */}
                <div className="flex justify-end mt-4">
                  <button
                    type="button"
                    onClick={addFarm}
                    className="bg-yellowcolor hover:bg-yellowcolor/80 text-black px-6 flex flex-row items-center gap-2 py-2 rounded-md capitalize"
                  >
                    <FiPlusCircle size={20} />
                    Add Farm
                  </button>
                </div>

                {farms.map((farm, index) => (
                  <div key={farm.id} className="my-4 border rounded-lg dark:border-border-dark">
                    {/* Header */}
                    <div
                      className={`flex items-center justify-between px-4 py-3 cursor-pointer ${currentFarmIndex === index ? 'bg-yellowcolor/30' : 'bg-gray-100 dark:bg-secondaryDark'}`}
                      onClick={() => setCurrentFarmIndex(index)}
                    >
                      <div>
                        <span className="font-semibold dark:text-white">Farm {index + 1}</span>
                        {farm.farm_name && (
                          <span className="ml-2 text-gray-600 dark:text-white">({farm.farm_name})</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {farms.length > 1 && (
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation()
                              removeFarm(index)
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <IoCloseCircleOutline size={20} />
                          </button>
                        )}
                        <span>{currentFarmIndex === index ? "▼" : "▶"}</span>
                      </div>
                    </div>
                    {/* Expanded Form */}
                    {currentFarmIndex === index && (
                      <div className="p-4 bg-white dark:bg-dark">
                        <div className="flex flex-col space-y-4 md:grid md:grid-cols-2 md:gap-7">
                          <div className="w-full space-y-3 mt-4">
                            <label className="text-sm font-normal capitalize dark:text-white">
                              Farm Name
                            </label>
                            <input
                              type="text"
                              placeholder="Enter your farm name"
                              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                              value={farm.farm_name || ""}
                              onChange={e => updateFarm(index, "farm_name", e.target.value)}
                              onBlur={e => updateFarm(index, "farm_name", e.target.value.trim())}
                            />
                            {farmErrors[index]?.farm_name && <p className="text-red-500 text-xs mt-1">{farmErrors[index].farm_name}</p>}
                          </div>
                          <div className="w-full space-y-3">
                            <label className="text-sm font-normal capitalize dark:text-white">
                              Farm Size (Acres)<span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              min={1}
                              placeholder="Enter your farm size"
                              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                              value={farm.farm_size || ""}
                              onChange={e => updateFarm(index, "farm_size", e.target.value)}
                            />
                            {farmErrors[index]?.farm_size && <p className="text-red-500 text-xs mt-1">{farmErrors[index].farm_size}</p>}
                          </div>
                          <div className="w-full space-y-3">
                            <label className="text-sm font-normal capitalize dark:text-white">
                              Region<span className="text-red-500">*</span>
                            </label>
                            <select
                              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                              value={farm.farm_region || ""}
                              onChange={e => {
                                const updatedFarms = [...farms]
                                updatedFarms[index] = {
                                  ...updatedFarms[index],
                                  farm_region: e.target.value,
                                  farm_district: "",
                                  farm_community: "",
                                }
                                setFarms(updatedFarms)
                                if (farmErrors[index]?.farm_region) {
                                  const updatedErrors = [...farmErrors]
                                  updatedErrors[index] = { ...updatedErrors[index], farm_region: null }
                                  setFarmErrors(updatedErrors)
                                }
                              }}
                            >
                              <option value="">Select Region</option>
                              {regionNames.map((regionName, idx) => (
                                <option key={idx} value={regionName}>{regionName}</option>
                              ))}
                            </select>
                            {farmErrors[index]?.farm_region && <p className="text-red-500 text-xs mt-1">{farmErrors[index].farm_region}</p>}
                          </div>
                          <div className="w-full space-y-3">
                            <label className="text-sm font-normal capitalize dark:text-white">
                              District<span className="text-red-500">*</span>
                            </label>
                            <select
                              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                              value={farm.farm_district || ""}
                              onChange={e => {
                                const updatedFarms = [...farms]
                                updatedFarms[index] = {
                                  ...updatedFarms[index],
                                  farm_district: e.target.value,
                                  farm_community: "",
                                }
                                setFarms(updatedFarms)
                                if (farmErrors[index]?.farm_district) {
                                  const updatedErrors = [...farmErrors]
                                  updatedErrors[index] = { ...updatedErrors[index], farm_district: null }
                                  setFarmErrors(updatedErrors)
                                }
                              }}
                            >
                              <option value="">Select District</option>
                              {farmDistricts?.map((item, idx) => (
                                <option key={idx} value={item.name}>{item.name}</option>
                              ))}
                            </select>
                            {farmErrors[index]?.farm_district && <p className="text-red-500 text-xs mt-1">{farmErrors[index].farm_district}</p>}
                          </div>
                          <div className="w-full space-y-3">
                            <label className="text-sm font-normal capitalize dark:text-white">
                              Community<span className="text-red-500">*</span>
                            </label>
                            <select
                              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                              value={farm.farm_community || ""}
                              onChange={e => updateFarm(index, "farm_community", e.target.value)}
                            >
                              <option value="">Select Community</option>
                              {farmCommunities?.map((item, idx) => (
                                <option key={idx} value={item}>{item}</option>
                              ))}
                            </select>
                            {farmErrors[index]?.farm_community && <p className="text-red-500 text-xs mt-1">{farmErrors[index].farm_community}</p>}
                          </div>
                          <div className="w-full space-y-3">
                            <label className="text-sm font-normal capitalize dark:text-white">
                              Primary Commodity<span className="text-red-500">*</span>
                            </label>
                            <select
                              name="primary_crop"
                              id="primary_crop"
                              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                              value={farm.primary_crop || ""}
                              onChange={e => updateFarm(index, "primary_crop", e.target.value)}
                            >
                              <option value="">Select Primary Commodity</option>
                              {crops?.map((item) => <option className="capitalize" key={item?.id} value={item?.name}>{item?.name}</option>)}
                            </select>
                            {farmErrors[index]?.primary_crop && <p className="text-red-500 text-xs mt-1">{farmErrors[index].primary_crop}</p>}
                          </div>
                          <div className="w-full space-y-3">
                            <label className="text-sm font-normal capitalize dark:text-white">
                              Longitude
                            </label>
                            <input
                              name="longitude"
                              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                              value={farm.longitude || ""}
                              onChange={e => updateFarm(index, "longitude", e.target.value)}
                            />
                          </div>
                          <div className="w-full space-y-3">
                            <label className="text-sm font-normal capitalize dark:text-white">
                              Latitude
                            </label>
                            <input
                              name="latitude"
                              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                              value={farm.latitude || ""}
                              onChange={e => updateFarm(index, "latitude", e.target.value)}
                            />
                          </div>
                          <div className="w-full space-y-3">
                            <label className="text-sm font-normal capitalize dark:text-white">
                              Ownership Type<span className="text-red-500">*</span>
                            </label>
                            <select
                              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent capitalize placeholder-gray-400"
                              value={farm.ownership || ""}
                              onChange={e => updateFarm(index, "ownership", e.target.value)}
                            >
                              <option value="">Select Ownership Type</option>
                              {OWNERSHIPTYPES?.map((item) => <option className="capitalize" key={item} value={item}>{item}</option>)}
                            </select>
                            {farmErrors[index]?.ownership && <p className="text-red-500 text-xs mt-1">{farmErrors[index].ownership}</p>}
                          </div>
                          <div></div>
                          <div className="w-full space-y-3 col-span-2">
                            <label className="text-sm font-normal capitalize dark:text-white">
                              Add Other Crops/ Commodities
                            </label>
                            <div className="flex flex-row items-center gap-4 flex-wrap">
                              {crops?.map((item, idx) => (
                                <button
                                  type="button"
                                  key={idx}
                                  className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-full py-2 px-3 flex flex-row items-center gap-2 text-sm"
                                  onClick={() => toggleFarmCrop(index, item?.id)}
                                >
                                  <p className="text-black dark:text-white">{item?.name}</p>
                                  {farm.other_crops.includes(item?.id) ?
                                    <FaRegCheckCircle className="w-4 h-4 text-yellowcolor" /> :
                                    <div className="w-4 h-4 border border-gray-500 rounded-full" />
                                  }
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="w-full space-y-3 mt-5">
                          <label className="text-sm font-normal capitalize dark:text-white">
                            Upload photos (Farm Images)
                          </label>
                          <div className="bg-gray-100 h-52 flex items-center justify-center rounded-md p-4 dark:bg-secondaryDark">
                            <label htmlFor={`farm_images_${index}`} className="cursor-pointer">
                              <div className="bg-white px-20 border border-dashed border-gray-200 rounded-md p-4 flex flex-col items-center justify-center gap-4 dark:bg-dark dark:border-border-dark ">
                                <svg width="47" height="46" viewBox="0 0 47 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <rect x="3.5" y="3" width="40" height="40" rx="20" fill="#F2F4F7" />
                                  <rect x="3.5" y="3" width="40" height="40" rx="20" stroke="#F9FAFB" stroke-width="6" />
                                  <path d="M20.168 26.3333L23.5013 23M23.5013 23L26.8346 26.3333M23.5013 23V30.5M30.168 26.9524C31.1859 26.1117 31.8346 24.8399 31.8346 23.4167C31.8346 20.8854 29.7826 18.8333 27.2513 18.8333C27.0692 18.8333 26.8989 18.7383 26.8064 18.5814C25.7197 16.7374 23.7133 15.5 21.418 15.5C17.9662 15.5 15.168 18.2982 15.168 21.75C15.168 23.4718 15.8642 25.0309 16.9904 26.1613" stroke="#475467" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                                <p className="text-sm text-black dark:text-white font-light text-center">
                                  Browse or drag and drop farm photos you want <br /> to upload from your computer
                                </p>
                                <p className="text-base text-yellowcolor underline text-center">
                                  Click to upload
                                </p>
                              </div>
                              <input
                                type="file"
                                id={`farm_images_${index}`}
                                className="hidden"
                                accept="image/png,image/jpeg"
                                multiple
                                onChange={e => handleFarmImageChange(e, index)}
                                onKeyDown={e => {
                                  if (e.key === "Enter") e.preventDefault()
                                }}
                              />
                            </label>
                          </div>
                        </div>
                        {farm.farm_images?.length > 0 && <div className="w-full mt-5">
                          <div className="bg-gray-100 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 rounded-md p-4 dark:bg-secondaryDark">
                            {farm.farm_images?.map((item, imgIdx) => (
                              <div key={imgIdx} className="bg-white h-32 relative group rounded-md overflow-hidden">
                                <img src={URL.createObjectURL(item)} alt="farm image" className="w-full h-full object-cover" />
                                <button type="button"
                                  onClick={() => removeFarmImage(index, imgIdx)}
                                  className="absolute top-0 right-0 bg-black/50 text-red-500 p-2 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                  <FaTrash className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>}
                        <hr className="my-5 border-gray-200" />
                        <div className="w-full space-y-3">
                          <label className="text-sm font-normal capitalize dark:text-white">
                            Farm Documents (Optional)
                          </label>
                          <div className="bg-gray-100 h-52 flex items-center justify-center rounded-md p-4 dark:bg-secondaryDark">
                            <label htmlFor={`farm_documents_${index}`} className="cursor-pointer">
                              <div className="bg-white px-20 border border-dashed border-gray-200 rounded-md p-4 flex flex-col items-center justify-center gap-4 dark:bg-dark dark:border-border-dark">
                                <svg width="47" height="46" viewBox="0 0 47 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <rect x="3.5" y="3" width="40" height="40" rx="20" fill="#F2F4F7" />
                                  <rect x="3.5" y="3" width="40" height="40" rx="20" stroke="#F9FAFB" stroke-width="6" />
                                  <path d="M20.168 26.3333L23.5013 23M23.5013 23L26.8346 26.3333M23.5013 23V30.5M30.168 26.9524C31.1859 26.1117 31.8346 24.8399 31.8346 23.4167C31.8346 20.8854 29.7826 18.8333 27.2513 18.8333C27.0692 18.8333 26.8989 18.7383 26.8064 18.5814C25.7197 16.7374 23.7133 15.5 21.418 15.5C17.9662 15.5 15.168 18.2982 15.168 21.75C15.168 23.4718 15.8642 25.0309 16.9904 26.1613" stroke="#475467" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                                <p className="text-sm text-black dark:text-white font-light text-center">
                                  Browse or drag and drop farm documents.
                                </p>
                                <p className="text-base text-yellowcolor underline text-center">
                                  Click to upload
                                </p>
                              </div>
                              <input
                                type="file"
                                id={`farm_documents_${index}`}
                                className="hidden"
                                accept="application/pdf"
                                multiple
                                onChange={e => handleFarmDocumentChange(e, index)}
                              />
                            </label>
                          </div>
                        </div>
                        {farm.farm_documents?.length > 0 && <div className="w-full mt-5">
                          <div className="bg-gray-100 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 rounded-md p-4 dark:bg-secondaryDark">
                            {farm.farm_documents?.map((item, docIdx) => (
                              <div key={docIdx} className="bg-white h-32 relative group rounded-md overflow-hidden flex flex-col items-center justify-center p-2">
                                <div className="flex flex-col items-center justify-center w-full h-full">
                                  {/* PDF Icon */}
                                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect width="24" height="24" rx="4" fill="#F2F4F7" />
                                    <path d="M7 2C5.89543 2 5 2.89543 5 4V20C5 21.1046 5.89543 22 7 22H17C18.1046 22 19 21.1046 19 20V8.82843C19 8.29799 18.7893 7.78929 18.4142 7.41421L14.5858 3.58579C14.2107 3.21071 13.702 3 13.1716 3H7Z" fill="#E53E3E" />
                                    <text x="7" y="28" font-size="8" fill="#fff">PDF</text>
                                  </svg>
                                  {/* File name */}
                                  <span className="block text-xs text-center mt-2 truncate w-24" title={item.name}>{item.name}</span>
                                </div>
                                {/* View/Download link */}
                                <a
                                  href={URL.createObjectURL(item)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 underline mt-1"
                                >
                                  View
                                </a>
                                {/* Remove button */}
                                <button
                                  type="button"
                                  onClick={() => removeFarmDocument(index, docIdx)}
                                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                                >
                                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M6 6L14 14M6 14L14 6" stroke="white" strokeWidth="2" strokeLinecap="round" /></svg>
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>}
                        <div className="h-8"></div>
                      </div>
                    )}
                  </div>
                ))}

              </div>,
            }[step]}
          </div>

          <div className="border-t z-50 border-gray-200 flex flex-row items-center justify-between w-full p-4 dark:border-border-dark">
            {step > 0 ? <button
              type="button"
              onClick={() => setStep(step - 1)}
              className='px-6 text-sm py-2 border border-yellowcolor text-yellowcolor rounded-lg'>
              Back
            </button> : <div></div>}

            <div className="flex flex-row items-end justify-end gap-4">
              <button
                type="button"
                onClick={() => history.goBack()}
                className='px-6 text-sm py-2 bg-white dark:bg-dark border dark:border-border-dark dark:text-white text-black rounded-lg'>
                Cancel
              </button>

              <button
                type={step === steps.length - 1 ? "button" : "button"}
                onClick={step === steps.length - 1 ? handleFinalSubmit : async () => {
                  // Step 0 fields (personal info)
                  const step0Fields = [
                    "firstname", "lastname", "contact", "region", "district", "community", "group", "gender"
                  ]

                  if (step === 0) {
                    const valid = await trigger(step0Fields)


                    if (valid) {
                      setStep(step + 1)
                    }
                  }
                }}
                className='px-6 text-sm py-2 bg-yellowcolor text-black rounded-lg'
                disabled={loading}
              >
                {step === steps.length - 1 ? (loading ? "Please wait..." : "Done") : "Next"}
              </button>
            </div>
          </div>

        </form>
      </div>
    </>
  )
}
